import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CurrentsAPI, type OceanAgent } from "@vida/currents-core";
import { createCurrentsApiConfig } from "../services/currentsSession";
import { roleLabel } from "../services/agentCatalog";
import { AgentAvatar } from "../components/AgentAvatar";

type ViewMode = "grid" | "list";

const VIEW_KEY = "vida_currents_agent_view";

function AgentRow({ agent }: { agent: OceanAgent }) {
  return (
    <li className="agent-list-row">
      <div className="agent-list-main">
        <AgentAvatar agent={agent} size="md" />
        <div>
          <strong>{agent.name}</strong>{" "}
          <span className="chip">{roleLabel(agent.role)}</span>
          {!agent.enabled && <span className="muted"> (disabilitato)</span>}
          <div className="muted agent-meta">{agent.modelId}</div>
        </div>
      </div>
      <div className="agent-actions">
        <Link className="btn secondary" to={`/agents/${agent.id}/edit`}>
          Modifica
        </Link>
        <Link className="btn" to={`/agents/${agent.id}/test`}>
          Testa
        </Link>
      </div>
    </li>
  );
}

function AgentCard({ agent }: { agent: OceanAgent }) {
  return (
    <article className="agent-card">
      <div className="agent-card-icon">
        <AgentAvatar agent={agent} size="lg" />
      </div>
      <h3 className="agent-card-title">{agent.name}</h3>
      <p className="agent-card-role">
        <span className="chip">{roleLabel(agent.role)}</span>
        {!agent.enabled && <span className="muted"> disabilitato</span>}
      </p>
      {agent.description && <p className="muted agent-card-desc">{agent.description}</p>}
      <p className="muted agent-meta">{agent.modelId}</p>
      <div className="agent-card-actions">
        <Link className="btn secondary" to={`/agents/${agent.id}/edit`}>
          Modifica
        </Link>
        <Link className="btn" to={`/agents/${agent.id}/test`}>
          Testa
        </Link>
      </div>
    </article>
  );
}

export function AgentListPage() {
  const [agents, setAgents] = useState<OceanAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(() => {
    const stored = localStorage.getItem(VIEW_KEY);
    return stored === "list" ? "list" : "grid";
  });

  useEffect(() => {
    void (async () => {
      try {
        const items = await CurrentsAPI.listAgents(createCurrentsApiConfig());
        setAgents(items);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function setViewMode(mode: ViewMode) {
    setView(mode);
    localStorage.setItem(VIEW_KEY, mode);
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Agenti</h1>
          <p className="muted">Crea e configura gli agenti Oceany della tua organizzazione.</p>
        </div>
        <div className="header-actions">
          <div className="view-toggle" role="group" aria-label="Modalità visualizzazione">
            <button
              type="button"
              className={view === "grid" ? "active" : "secondary"}
              onClick={() => setViewMode("grid")}
              aria-pressed={view === "grid"}
            >
              Griglia
            </button>
            <button
              type="button"
              className={view === "list" ? "active" : "secondary"}
              onClick={() => setViewMode("list")}
              aria-pressed={view === "list"}
            >
              Lista
            </button>
          </div>
          <Link className="btn secondary" to="/agents/new?template=retrieval">
            Agente Retrieval
          </Link>
          <Link className="btn secondary" to="/agents/new?template=illustrator">
            Agente Illustrator
          </Link>
          <Link className="btn" to="/agents/new">
            Nuovo agente
          </Link>
        </div>
      </header>

      {loading && <p className="muted">Caricamento…</p>}
      {error && <p className="err">{error}</p>}

      {!loading && !error && agents.length === 0 && (
        <div className="card">
          <p className="muted">Nessun agente. Creane uno per iniziare.</p>
        </div>
      )}

      {!loading && !error && agents.length > 0 && view === "list" && (
        <div className="card">
          <ul className="agent-list">
            {agents.map((a) => (
              <AgentRow key={a.id} agent={a} />
            ))}
          </ul>
        </div>
      )}

      {!loading && !error && agents.length > 0 && view === "grid" && (
        <div className="agent-grid">
          {agents.map((a) => (
            <AgentCard key={a.id} agent={a} />
          ))}
        </div>
      )}
    </div>
  );
}
